/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/naming-convention, import/namespace, @typescript-eslint/explicit-module-boundary-types, no-underscore-dangle */
import { Injectable } from '@angular/core';
import { setUpLocationSync } from '@angular/router/upgrade';
import { UpgradeModule } from '@angular/upgrade/static';
import { BehaviorSubject } from 'rxjs';
import 'angular';
import 'angular-route';
import { AJS_MODULE_NAME } from '@common/constants';
/**
 * AngularJS Service
 * - bootstraps AngularJS in host Angular
 * - able to retrieve AngularJS providers and use within Angular
 * - synchronizes AngularJS routing system with Angular
 */
@Injectable()
export class AJSService {
	public loaded$ = new BehaviorSubject(false);

	/**
	 * Instance of AngularJS injector
	 */
	public $injector: angular.auto.IInjectorService;

	public portal: any;

	constructor(private readonly upgrade: UpgradeModule) {}

	/**
	 * Gets current route associated with angularjs router
	 */
	public get currentRoute() {
		const $route = this.getProvider<angular.route.IRouteService>(AJS_PROVIDERS.$ROUTE);
		if (!$route) {
			return null;
		}
		return $route.current;
	}

	private _ajsRoutes: any[] = [];
	/**
	 * AngularJS routes
	 */
	public get ajsRoutes(): any[] {
		const $route = this.getProvider<angular.route.IRouteService>(AJS_PROVIDERS.$ROUTE);
		if (!$route) {
			return [];
		}
		if (this._ajsRoutes.length > 0) {
			return this._ajsRoutes;
		}
		this._ajsRoutes = Object.values($route.routes);
		return this._ajsRoutes;
	}

	public isAuthenticatedAJSRoute(url: string): boolean {
		let parsedUrl = url;
		try {
			// url may contain http://
			parsedUrl = new URL(url).pathname;
		} catch {
			// do nothing
		}

		if (parsedUrl.indexOf('?') >= 0) {
			parsedUrl = parsedUrl.split('?')[0];
		}

		const urls = [parsedUrl];

		if (!parsedUrl.startsWith('/')) {
			urls.push(`/${parsedUrl}`);
		}

		if (!parsedUrl.endsWith('/')) {
			urls.push(`${parsedUrl}/`);
		}

		return this.ajsRoutes.some((route) => urls.includes(route.originalPath) || urls.some((u) => route.regexp?.test(u)));
	}

	ajsRouteCache = new Map<string, boolean>();

	public isAJSRoute(url: string): boolean {
		let parsedUrl = url;
		try {
			// url may contain http://
			parsedUrl = new URL(url).pathname;
		} catch {
			// do nothing
		}

		if (parsedUrl.indexOf('?') >= 0) {
			parsedUrl = parsedUrl.split('?')[0];
		}

		const urls = [parsedUrl];

		if (!parsedUrl.startsWith('/')) {
			urls.push(`/${parsedUrl}`);
		}

		if (!parsedUrl.endsWith('/')) {
			urls.push(`${parsedUrl}/`);
		}

		if (!this.ajsRouteCache.has(parsedUrl)) {
			this.ajsRouteCache.set(
				parsedUrl,
				this.ajsRoutes.some((route) => urls.includes(route.originalPath) || urls.some((u) => route.regexp?.test(u))),
			);
		}

		return this.ajsRouteCache.get(parsedUrl);
	}

	/**
	 * Observable that reflects if AngularJS app has laoded
	 */

	/**
	 * Bootstraps AngularJS in host Angular
	 *
	 * @param node - Mount node
	 */
	public async bootstrap(node: any): Promise<void> {
		this.upgrade.bootstrap(node, [AJS_MODULE_NAME], { strictDi: true });
		this.$injector = this.upgrade.$injector;
		setUpLocationSync(this.upgrade);
		this.loaded$.next(true);
	}

	/**
	 * Unload AngularJS
	 */
	public destroy(): void {
		const $rootScope = this.getProvider<angular.IRootScopeService>(AJS_PROVIDERS.$ROOT_SCOPE);
		$rootScope.$destroy();
		this.loaded$.next(false);
	}

	/**
	 * Retrieves AngularJS provider
	 *
	 * @example
	 * const ajsAuthService = getProvider<IAJSAuthService>('authService')
	 * @param key
	 */
	public getProvider<T = any>(key: string): T {
		if (this.$injector?.has(key)) {
			return this.$injector.get(key);
		}
		return null;
	}
}

/* eslint-disable @typescript-eslint/naming-convention */
export const AJS_PROVIDERS = {
	$ROUTE: '$route',
	$ROOT_SCOPE: '$rootScope',
	$LOCATION: '$location',

	AUTH_SERVICE: 'authService',
	AUTH_INTERCEPTOR_SERVICE: 'authInterceptorService',

	DIALOG_HELPER: 'dialogHelper',
};
